import React from "react";
import useForm from "react-hook-form";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CREATE_FEEDBACK_REPORT_ISSUE_REQUEST } from "redux/reducers/feedback/actionTypes";
import { reducerType } from "redux/reducers";

import ButtonOutline from "components/ButtonOutline";
import SubmitButton from "components/Button";
import ErrorMessage from "components/ErrorMessage";
import Input from "components/Input";
import useStyles from "./styles";
import imgLoading from "asset/img/loading.gif";

interface ModalVolunteerReportProps {
  open: boolean;
  setOpen: Function;
  serviceId?: string;
}

const ModalVolunteerReport = React.memo((props: ModalVolunteerReportProps) => {
  const classes = useStyles();
  const { open, setOpen, serviceId } = props;
  const dispatch = useDispatch();
  const translate = useTranslation().t;
  const { register, errors, handleSubmit } = useForm();
  const [selectedIssue, setSelectedIssue] = React.useState("Duplicated");
  const [loading, setLoading] = React.useState(false);
  const messageState = useSelector((state: reducerType) => state.message);

  const submit = (data: any, e: any) => {
    e.preventDefault();
    setLoading(true);
    dispatch({
      type: CREATE_FEEDBACK_REPORT_ISSUE_REQUEST,
      form: {
        subject: selectedIssue, // "Duplicated/Incorrect",
        message: data.message,
        service: serviceId
      },
      message: translate("SENT_FEEDBACK_REPORT_ISSUE_SUCCESSFULLY")
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    if (open) {
      setSelectedIssue("Duplicated");
      setLoading(false);
    }
  }, [open]);

  // Listen for success message to close modal
  React.useEffect(() => {
    if (loading && messageState.hasMessage) {
      if (
        messageState.type === "success" &&
        messageState.key === "SENT_FEEDBACK_REPORT_ISSUE_SUCCESSFULLY"
      ) {
        setTimeout(() => {
          setOpen(false);
          setLoading(false);
        }, 500);
      } else if (messageState.type === "error") {
        setLoading(false);
      }
    }
  }, [messageState, loading, setOpen]);

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={open}
        onClose={() => handleClose()}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={open}>
          <div className={classes.paper}>
            <h2 className={classes.title}>{translate("REPORT_ISSUE")}</h2>
            <form
              className={classes.root}
              name="feedback"
              onSubmit={handleSubmit(submit)}
            >
              <div className={classes.label}>{translate("ISSUE")}</div>
              <RadioGroup
                aria-label="issue"
                name="subject"
                value={selectedIssue}
                onChange={e => {
                  setSelectedIssue(e.target.value);
                }}
              >
                <FormControlLabel
                  value="Duplicated"
                  control={<Radio className={classes.radio} />}
                  label={translate("REPORT_SERVICE_AS_DUPLICATE")}
                  className={classes.radioLabel}
                />
                <FormControlLabel
                  value="Incorrect"
                  control={<Radio className={classes.radio} />}
                  label={translate("REPORT_MISSING_OR_INCORRECT_INFO")}
                  className={classes.radioLabel}
                />
              </RadioGroup>

              <Input
                name="message"
                type="textarea"
                fullWidth
                label={translate("MESSAGE")}
                validate={register({
                  required: true
                })}
                error={errors.message}
              />
              {errors.message && errors.message.type === "required" && (
                <ErrorMessage>
                  {translate("REQUIRED_INPUT_CTA", {
                    value: translate("MESSAGE")
                  })}
                </ErrorMessage>
              )}

              <Grid
                style={{ paddingTop: "20px" }}
                container
                spacing={1}
                direction="row"
              >
                <Grid item xs={6} sm={6} md={6} lg={6}>
                  <ButtonGroup onClick={handleClose} fullWidth>
                    <ButtonOutline variant="contained">
                      {translate("CANCEL")}
                    </ButtonOutline>
                  </ButtonGroup>
                </Grid>
                <Grid item xs={6} sm={6} md={6} lg={6}>
                  <ButtonGroup fullWidth>
                    <SubmitButton
                      type="submit"
                      variant="contained"
                      disabled={loading}
                    >
                      {loading ? (
                        <img alt="loading" src={imgLoading} width="30px" />
                      ) : (
                        translate("SEND")
                      )}
                    </SubmitButton>
                  </ButtonGroup>
                </Grid>
              </Grid>
            </form>
          </div>
        </Fade>
      </Modal>
    </div>
  );
});

export default ModalVolunteerReport;
